import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { TrendingUp } from 'lucide-react';
import axios from 'axios';
import { BASE_URL } from '../apiConfig';
import Cookies from 'js-cookie';
import SelectDate from './SelectDateCharts';
import { useTranslation } from '../LanguageContext';

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const value = payload[0].value;
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-lg dark:border-gray-700 dark:bg-gray-800">
        <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
        <p className={`text-sm font-bold ${value >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
          {value.toFixed(2)} MAD
        </p>
      </div>
    );
  }
  return null;
};

export default function DailyProfitChart() {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = Cookies.get('WEB_SSR');

  const fetchData = async (startDate, endDate) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('date_from', startDate);
      if (endDate) params.append('date_to', endDate);

      const response = await axios.get(`${BASE_URL}/api/chart-stats?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      });

      if (response.data.success) {
        const profitData = response.data.data.daily_profit.map((item) => ({
          date: item.date,
          profit: Number(item.profit),
        }));
        setData(profitData);
      }
    } catch (error) {
      console.error('Error fetching profit chart data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (range) => {
    fetchData(range.start_date, range.end_date);
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    const months = t('date.months_short');
    const monthsArray = Array.isArray(months) ? months : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${d.getDate()} ${monthsArray[d.getMonth()]}`;
  };

  const totalProfit = data.reduce((sum, item) => sum + item.profit, 0);

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-900">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-xl">
            <TrendingUp className="w-5 h-5 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-50">{t('charts.daily_profit')}</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('charts.total')}: <span className={`font-bold ${totalProfit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>{totalProfit.toFixed(2)} MAD</span>
            </p>
          </div>
        </div>
        <SelectDate type="chart-profit" onDateRangeChange={handleDateRangeChange} />
      </div>

      {loading ? (
        <div className="h-64 flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-gray-200 border-t-gray-900 dark:border-gray-700 dark:border-t-gray-100 rounded-full animate-spin" />
        </div>
      ) : data.length === 0 ? (
        <div className="h-64 flex items-center justify-center text-sm text-gray-400">
          {t('charts.no_data')}
        </div>
      ) : (
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                tick={{ fontSize: 11, fill: '#9ca3af' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#9ca3af' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => v.toFixed(0)}
              />
              <Tooltip content={<CustomTooltip />} />
              <ReferenceLine y={0} stroke="#e5e7eb" strokeDasharray="3 3" />
              <Line
                type="monotone"
                dataKey="profit"
                stroke="#16a34a"
                strokeWidth={1.5}
                dot={data.length < 20 ? { r: 2.5, fill: '#16a34a', strokeWidth: 1, stroke: '#fff' } : false}
                activeDot={{ r: 4, strokeWidth: 1, stroke: '#fff' }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
